import React, {useState} from 'react';
import {useAsync} from "react-async-hook";
import {useLocation} from "react-router-dom";
import {useForm} from "react-hook-form";
import {useSystemMessage} from "../hooks/useSystemMessage";
import useAppRouter from "../hooks/useAppRouter";
import useLoading from '../hooks/useLoading';
import {userApi} from "../apis/user/userApi";
import {PasswordDisplayButton} from "../components/pages/common/PasswordDisplayButton";

import passwordIcon from '../assets/images/password_icon.png';
import {FormValidationType, makeRegisterOptions} from "../libs/reactHookForm/makeRegisterOptions";
import {ValidationErrorMessages} from "../components/pages/common/ValidationErrorMessages";
import {ApiClientError} from "../apis/errors";
import {logError} from "../libs/utils/logError";

export interface PasswordResetFormValues {
  password: string;
  confirm: string;
}

export const PasswordResetEditPage: React.FC = () => {
  const {success} = useSystemMessage();
  const [abortController, setAbortController] = useState<AbortController | undefined>();
  const withLoading = useLoading();
  const location = useLocation();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const token = useQuery().get('token');
  const { goto } = useAppRouter();
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onSubmit',
  });
  const password = watch('password');

  const [ isShowPassword, setIsShowPassword ] = React.useState<boolean>(false);
  const [ isShowConfirmPassword, setIsShowConfirmPassword ] = React.useState<boolean>(false);
  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);

  useAsync(async () => {
    if (token === null) {
      goto('notFoundError');
    }
  }, []);

  const handleUpdate = async () => {
    withLoading(async () => {
      setApiClientError(null);
      await handleSubmit(async (data: PasswordResetFormValues) => {
        try {
          setAbortController(new AbortController());
          await userApi.sendPasswordReset({
            password: data.password,
            token: token!,
          }, { abortSignal: abortController?.signal });
          setAbortController(undefined);
          await success('パスワードを変更しました。');
          goto('login');

        } catch (e) {
          await logError(
            location.pathname,
            'パスワードの変更に失敗しました。',
            e
          );

          if (e instanceof ApiClientError) {
            setApiClientError(e);
            return;
          }

          goto('internalServerError');
        }
      })();
    });
  };

  return (
    <>
      <form className="bg-primary flex flex-col min-w-full min-h-full justify-between py-12 px-16">
        <div>
          <div className="text-2xl text-white font-bold">パスワード再設定</div>
          <hr className="w-8 border-4 border-white mt-2" />
          <div className="mt-1 text-danger-secondary text-xs">
            { apiClientError?.message }
            { (Object.keys(errors).length > 0) && '入力エラーがあります' }
          </div>
        </div>

        <div>
          { /* 新しいパスワード */ }
          <div className="mb-12">
            <label htmlFor="password">
              <div className="flex items-center">
                <span className="w-3 mr-1">
                  <img src={passwordIcon} className="object-contain" alt="icon" />
                </span>
                <span className="relative flex-grow">
                  <input
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...register("password",
                      makeRegisterOptions('パスワード', [
                        { type: FormValidationType.REQUIRED },
                        { type: FormValidationType.MAX_STRING_LENGTH, value: 64 },
                        { type: FormValidationType.PASSWORD, typeName: 'password' },
                      ])
                    )}
                    id="password"
                    className="min-w-full bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none p-2"
                    type={isShowPassword ? "text" : "password"}
                    maxLength={64}
                    placeholder="新しいパスワード"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 text-white flex items-center text-sm leading-5">
                    <PasswordDisplayButton
                      {...{
                        isShow: isShowPassword,
                        setIsShow: setIsShowPassword,
                      }}
                    />
                  </div>
                </span>
              </div>
              <div className="ml-6">
                <div className="mt-1 text-danger-secondary text-xs">
                  {errors.password?.message}
                </div>
                <ValidationErrorMessages
                  {...{
                    fieldErrors: apiClientError?.errors,
                    field: 'password',
                    textColorClass: 'text-danger-secondary',
                  }}
                />
              </div>
            </label>
          </div>

          { /* 新しいパスワード（確認） */ }
          <div className="mb-4">
            <label htmlFor="confirm">
              <div className="flex items-center">
                <span className="w-3 mr-1">
                  <img src={passwordIcon} className="object-contain" alt="icon" />
                </span>
                <span className="relative flex-grow">
                  <input
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...register("confirm",
                      makeRegisterOptions('パスワード（確認）', [
                        { type: FormValidationType.REQUIRED },
                        { type: FormValidationType.MAX_STRING_LENGTH, value: 64 },
                        { type: FormValidationType.PASSWORD, typeName: 'password' },
                        {
                          type: FormValidationType.CONFIRMED,
                          otherFieldName: 'パスワード',
                          otherFieldValue: password,
                          typeName: 'confirmedPassword',
                        },
                      ])
                    )}
                    id="confirm"
                    className="min-w-full bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none p-2"
                    type={isShowConfirmPassword ? "text" : "password"}
                    maxLength={64}
                    placeholder="新しいパスワード（確認）"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 text-white flex items-center text-sm leading-5">
                    <PasswordDisplayButton
                      {...{
                        isShow: isShowConfirmPassword,
                        setIsShow: setIsShowConfirmPassword,
                      }}
                    />
                  </div>
                </span>
              </div>
              <div className="ml-6">
                <div className="mt-1 text-danger-secondary text-xs">
                  {errors.confirm?.message}
                </div>
              </div>
            </label>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            className="btn-primary text-6xl"
            type="button"
            onClick={handleUpdate}
          >
            更新
          </button>
        </div>

      </form>
    </>
  );
};
